import React, { useState, useEffect } from 'react';
import { FaSave, FaTimes, FaCheckCircle, FaFilePdf, FaCalculator } from 'react-icons/fa';

const WyomingChecklistForm = ({ initialData, onSave, onCancel, isSubmitting }) => {
  const defaultData = {
    mailingCountry: '',
    mailingAddr1: '',
    mailingAddr2: '',
    mailingCity: '',
    mailingState: '',
    mailingPostalCode: '',
    principalCountry: '',
    principalAddr1: '',
    principalAddr2: '',
    principalCity: '',
    principalState: '',
    principalPostalCode: '',
    principalPhone: '',
    principalEmail: '',
    cash: '0',
    tradeNotes: '0',
    subtractAllowance: '0',
    inventories: '0',
    usGovObligations: '0',
    taxExemptSecurities: '0',
    otherCurrentAssets: '0',
    loansToStockholders: '0',
    mortgageRealEstateLoans: '0',
    otherInvestments: '0',
    depreciableAssets: '0',
    depletableAssets: '0',
    land: '0',
    intangibleAssets: '0',
    subtractAmortization: '0',
    otherAssets: '0',
    totalAssetValue: '0',
    filingFee: '62.00',
    certifyFirstName: '',
    certifyMiddleName: '',
    certifyLastName: '',
    certifyEmail: ''
  };

  const [formData, setFormData] = useState({
    ...defaultData,
    ...(initialData || {})
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Recalculate total assets and filing fee if any asset field changes
      const assetFields = [
        'cash', 'tradeNotes', 'inventories', 'usGovObligations', 
        'taxExemptSecurities', 'otherCurrentAssets', 'loansToStockholders', 
        'mortgageRealEstateLoans', 'otherInvestments', 'depreciableAssets', 
        'depletableAssets', 'land', 'intangibleAssets', 'otherAssets'
      ];
      
      if (assetFields.includes(name) || name === 'subtractAllowance' || name === 'subtractAmortization') {
        const sum = assetFields.reduce((acc, field) => acc + parseFloat(newData[field] || 0), 0);
        const subtractions = parseFloat(newData.subtractAllowance || 0) + parseFloat(newData.subtractAmortization || 0);
        const total = Math.max(0, sum - subtractions);
        newData.totalAssetValue = total.toFixed(2);
        
        // Calculation: Max($60, total * 0.0002) + $2
        const calculatedFee = Math.max(60, total * 0.0002) + 2;
        newData.filingFee = calculatedFee.toFixed(2);
      }
      
      return newData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, true);
  };

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-dark-700">
      <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center bg-gradient-to-r from-indigo-600 to-violet-600">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FaCheckCircle /> Wyoming Annual Report Checklist
        </h2>
        <button onClick={onCancel} className="text-white/80 hover:text-white transition-colors">
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Section A: Mailing Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">A</span>
              Mailing Address
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Country</label>
                <input type="text" name="mailingCountry" value={formData.mailingCountry} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="USA" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 1</label>
                <input type="text" name="mailingAddr1" value={formData.mailingAddr1} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 2 (Optional)</label>
                <input type="text" name="mailingAddr2" value={formData.mailingAddr2} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
                <input type="text" name="mailingCity" value={formData.mailingCity} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">State/Province</label>
                <input type="text" name="mailingState" value={formData.mailingState} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Postal Code</label>
                <input type="text" name="mailingPostalCode" value={formData.mailingPostalCode} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
              </div>
            </div>
          </div>

          {/* Section B: Principal Office Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 flex items-center justify-center text-sm font-bold">B</span>
              Principal Office Address
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Country</label>
                <input type="text" name="principalCountry" value={formData.principalCountry} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" placeholder="USA" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 1</label>
                <input type="text" name="principalAddr1" value={formData.principalAddr1} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 2 (Optional)</label>
                <input type="text" name="principalAddr2" value={formData.principalAddr2} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
                <input type="text" name="principalCity" value={formData.principalCity} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">State/Province</label>
                <input type="text" name="principalState" value={formData.principalState} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Postal Code</label>
                <input type="text" name="principalPostalCode" value={formData.principalPostalCode} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Phone</label>
                <input type="text" name="principalPhone" value={formData.principalPhone} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Email</label>
                <input type="email" name="principalEmail" value={formData.principalEmail} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none" required />
              </div>
            </div>
          </div>
        </div>

        {/* Section C: Filing Fees / Total Assets */}
        <div className="pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-sm font-bold">C</span>
            Filing Fees & Asset Worksheet
          </h3>
          <div className="bg-gray-50 dark:bg-dark-900/50 rounded-2xl p-6 border border-gray-100 dark:border-dark-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { label: '1. Cash', name: 'cash' },
                { label: '2a. Trade Notes & Accounts Receivable', name: 'tradeNotes' },
                { label: '2b. Subtract Allowance for Bad Debts', name: 'subtractAllowance', isMinus: true },
                { label: '3. Inventories', name: 'inventories' },
                { label: '4. U.S. Government Obligations', name: 'usGovObligations' },
                { label: '5. Tax Exempt Securities', name: 'taxExemptSecurities' },
                { label: '6. Other Current Assets', name: 'otherCurrentAssets' },
                { label: '7. Loans to Stockholders', name: 'loansToStockholders' },
                { label: '8. Mortgage and Real Estate Loans', name: 'mortgageRealEstateLoans' },
                { label: '9. Other Investments', name: 'otherInvestments' },
                { label: '10. Assets (real and personal)', name: 'depreciableAssets' },
                { label: '11. Depletable assets', name: 'depletableAssets' },
                { label: '12. Land (Assessed Valuation)', name: 'land' },
                { label: '13a. Intangible Assets', name: 'intangibleAssets' },
                { label: '13b. Subtract Accumulated Amortization', name: 'subtractAmortization', isMinus: true },
                { label: '14. Other Assets', name: 'otherAssets' },
              ].map((field) => (
                <div key={field.name} className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                    {field.isMinus && <span className="text-red-500 font-black">-</span>}
                    {field.label}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                    <input
                      type="number"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      className={`w-full pl-7 pr-4 py-2 rounded-xl border ${field.isMinus ? 'border-red-100 dark:border-red-900/30' : 'border-gray-200 dark:border-dark-600'} bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm`}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Certify Section */}
        <div className="pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">D</span>
            Certification
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">First Name</label>
              <input type="text" name="certifyFirstName" value={formData.certifyFirstName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Middle Name</label>
              <input type="text" name="certifyMiddleName" value={formData.certifyMiddleName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Last Name</label>
              <input type="text" name="certifyLastName" value={formData.certifyLastName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Certifier Email</label>
              <input type="email" name="certifyEmail" value={formData.certifyEmail} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-10 border-t border-gray-100 dark:border-dark-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-3 rounded-xl font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all text-center"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(formData, false)}
            disabled={isSubmitting}
            className="px-8 py-3 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <FaSave /> Save as Draft
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-10 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Submitting...</>
            ) : (
              <><FaFilePdf /> Submit Wyoming Checklist</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WyomingChecklistForm;
